/**
 * Option C add material / service insert (founder 2026-07-28).
 *
 * Shared by Section III CSF nests and Section II Project Details.
 *
 * Rules:
 * - Compact single row, 11px type
 * - Entry fields use a light fill so purpose / description / qty /
 *   unit cost read as required inputs (not plain text)
 * - Purpose: free text, 1–2 words
 * - Description: catalog search; pick locks description + MSKU (master
 *   product/service — not editable). Custom description without a pick
 *   is allowed (no MSKU).
 * - Qty: blank by default (engine/template-populated rows are separate);
 *   estimator enters or overrides
 * - Unit cost: from catalog on pick, always overridable
 */

import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { catalogApi, type CatalogSearchResult } from '@/services/catalog';

export interface AddSkuCommitPayload {
  purpose: string;
  description: string;
  /** Empty string when the estimator entered a custom description. */
  sku: string;
  quantity: string;
  unitCost: string;
  /** True when description + MSKU came from a catalog pick. */
  fromCatalog: boolean;
}

export interface AddSkuInlineProps {
  type: 'MATERIAL' | 'SERVICE';
  onCommit: (payload: AddSkuCommitPayload) => Promise<void>;
  onCancel: () => void;
}

function isNumericString(s: string): boolean {
  if (s.trim() === '') return false;
  return Number.isFinite(Number(s));
}

function formatMoney(s: string | undefined | null): string {
  if (s == null || s === '') return '—';
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  const hasCents = Math.abs(n % 1) > 0.004;
  return `$ ${n.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function AddSkuInline({ type, onCommit, onCancel }: AddSkuInlineProps) {
  const [purpose, setPurpose] = useState('');
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [fromCatalog, setFromCatalog] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<CatalogSearchResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Purpose + description + qty + unit cost always required.
  // MSKU required only when a catalog item was selected.
  const valid =
    purpose.trim() !== '' &&
    description.trim() !== '' &&
    (!fromCatalog || sku.trim() !== '') &&
    isNumericString(quantity.trim()) &&
    isNumericString(unitCost.trim());

  const lineTotal =
    isNumericString(quantity.trim()) && isNumericString(unitCost.trim())
      ? Number(quantity) * Number(unitCost)
      : null;

  const clearCatalogPick = () => {
    setFromCatalog(false);
    setSku('');
    // Keep description text so they can edit it as a custom entry.
  };

  const onDescriptionChange = (q: string) => {
    if (fromCatalog) return; // locked after master-product pick
    setDescription(q);
    if (sku) setSku('');
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.trim().length < 2) {
      setResults([]);
      setSearchOpen(false);
      return;
    }
    searchTimer.current = setTimeout(() => {
      catalogApi
        .search(q.trim(), type === 'MATERIAL' ? 'M' : 'S', 8)
        .then((rows) => {
          setResults(rows ?? []);
          setSearchOpen((rows ?? []).length > 0);
        })
        .catch(() => {
          setResults([]);
          setSearchOpen(false);
        });
    }, 200);
  };

  const pickResult = (r: CatalogSearchResult) => {
    setDescription(r.description);
    setSku(r.sku);
    setFromCatalog(true);
    if (r.unitCost != null) setUnitCost(String(r.unitCost));
    setSearchOpen(false);
    setResults([]);
  };

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onCommit({
        purpose: purpose.trim(),
        description: description.trim(),
        sku: sku.trim(),
        quantity: quantity.trim(),
        unitCost: unitCost.trim(),
        fromCatalog,
      });
    } finally {
      setBusy(false);
    }
  };

  // Light fill + soft border so empty required fields are obvious vs
  // surrounding statement text (founder UX ask 2026-07-29).
  const inputClass =
    'h-6 rounded-sm border border-outline-variant/70 bg-muted/45 px-1.5 font-public-sans text-[11px] shadow-none placeholder:text-muted-foreground/80 focus-visible:border-primary focus-visible:bg-card focus-visible:ring-1 focus-visible:ring-ring/40';

  return (
    <form
      className="my-1 border-l-2 border-l-primary/50 py-1 pl-2"
      data-testid="add-sku-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="mb-1 font-public-sans text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
        Add {type === 'MATERIAL' ? 'material' : 'service'}
      </p>
      <div
        className="flex flex-wrap items-center gap-x-2 gap-y-1 font-public-sans text-[11px]"
        data-testid="add-sku-row"
      >
        <Input
          autoFocus
          disabled={busy}
          value={purpose}
          placeholder="Purpose"
          aria-label="New SKU purpose"
          title="1–2 words"
          className={`${inputClass} w-[5.5rem] shrink-0`}
          onChange={(e) => setPurpose(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        />

        <div className="relative min-w-[12rem] flex-1">
          {fromCatalog ? (
            <span
              className="block truncate px-1 py-0.5 text-[11px] text-foreground"
              aria-label="New SKU description"
              title="From master product/service — not editable"
              data-testid="add-sku-description-locked"
            >
              {description}
            </span>
          ) : (
            <Input
              disabled={busy}
              value={description}
              placeholder={
                type === 'MATERIAL'
                  ? 'Search products or type custom…'
                  : 'Search services or type custom…'
              }
              aria-label="New SKU description"
              className={`${inputClass} w-full`}
              onChange={(e) => onDescriptionChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  if (searchOpen) setSearchOpen(false);
                  else onCancel();
                }
              }}
            />
          )}
          {searchOpen && results.length > 0 && !fromCatalog && (
            <ul
              className="absolute left-0 right-0 top-full z-20 mt-0.5 max-h-48 min-w-[16rem] overflow-y-auto border border-outline-variant bg-card shadow-md"
              data-testid="sku-search-results"
            >
              {results.map((r) => (
                <li key={`${r.type}-${r.sku}`}>
                  <Button
                    type="button"
                    variant="ghost"
                    className="flex h-auto w-full flex-col items-start gap-0 rounded-none px-2 py-1 text-left"
                    onClick={() => pickResult(r)}
                  >
                    <span className="max-w-full truncate text-[11px] text-foreground">
                      {r.description}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {r.sku}
                      {r.unitCost != null
                        ? ` · ${formatMoney(String(r.unitCost))}`
                        : ''}
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <span className="flex shrink-0 items-center gap-0.5">
          <span
            className={`font-mono text-[10px] ${
              sku
                ? 'bg-muted/60 px-1.5 py-0.5 text-foreground-soft'
                : 'px-1 text-muted-foreground'
            }`}
            aria-label="New SKU code"
            data-testid="add-sku-msku"
            title={
              sku
                ? 'From master product/service — not editable'
                : 'Optional — pick a catalog match, or leave blank for custom'
            }
          >
            {sku || '—'}
          </span>
          {fromCatalog && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={clearCatalogPick}
              className="h-5 px-1 py-0 text-[9px] text-muted-foreground hover:text-foreground"
              title="Clear catalog pick to enter a custom description"
            >
              Clear
            </Button>
          )}
        </span>

        <Input
          disabled={busy}
          value={quantity}
          placeholder="Qty"
          aria-label="New SKU quantity"
          className={`${inputClass} w-12 shrink-0 text-right font-mono`}
          onChange={(e) => setQuantity(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        />

        <Input
          disabled={busy}
          value={unitCost}
          placeholder="0.00"
          aria-label="New SKU unit cost"
          className={`${inputClass} w-16 shrink-0 text-right font-mono`}
          onChange={(e) => setUnitCost(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        />

        <span
          className="w-16 shrink-0 text-right font-mono text-[11px] font-semibold tabular-nums text-foreground"
          data-testid="add-sku-line-total"
        >
          {lineTotal != null ? formatMoney(String(lineTotal)) : '—'}
        </span>

        <span className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={onCancel}
            className="h-6 px-1.5 py-0 text-[11px] text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="ghost"
            size="sm"
            disabled={!valid || busy}
            className="h-6 px-1.5 py-0 text-[11px] font-semibold text-primary hover:text-primary-dim disabled:opacity-40"
          >
            Save
          </Button>
        </span>
      </div>
    </form>
  );
}
